import React, { useRef, useEffect, useMemo } from "react";
import { Image as KonvaImage, Group, Text } from "react-konva";
import useImage from "use-image";

// Factory: returns a Konva filter function that draws a border in the given hex color
function makeBorderFilter(hexColor = '#ff0000') {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const borderSize = 2;

  return function BorderFilter(imageData) {
    const w = imageData.width;
    const h = imageData.height;

    const imageCanvas = document.createElement('canvas');
    imageCanvas.width = w;
    imageCanvas.height = h;
    const imgCtx = imageCanvas.getContext('2d');
    imgCtx.putImageData(imageData, 0, 0);

    const borderCanvas = document.createElement('canvas');
    borderCanvas.width = w;
    borderCanvas.height = h;
    const borderCtx = borderCanvas.getContext('2d');
    borderCtx.shadowColor = `rgb(${r},${g},${b})`;
    borderCtx.shadowBlur = borderSize;
    borderCtx.drawImage(imageCanvas, 0, 0);

    const borderData = borderCtx.getImageData(0, 0, w, h);
    const origData = imageData.data;
    const bordData = borderData.data;

    for (let i = 0; i < origData.length; i += 4) {
      if (origData[i + 3] < 128 && bordData[i + 3] > 30) {
        origData[i] = r;
        origData[i + 1] = g;
        origData[i + 2] = b;
        origData[i + 3] = Math.min(255, bordData[i + 3] * 4);
      }
    }
  };
}

const UtilityIcon = ({ 
  x, 
  y, 
  id, 
  onDragMove, 
  onDragStart, 
  onDragEnd, 
  src, 
  size = 32, 
  draggable = true, 
  onContextMenu,
  number = null,
  onBadgeContext,
  borderColor = '#ff0000',
}) => {
  const [image] = useImage(src);
  const imgRef = useRef(null);

  // Recreate the filter whenever borderColor changes
  const borderFilter = useMemo(() => makeBorderFilter(borderColor), [borderColor]);

  // Re-cache whenever the image, size, or filter changes so the new filter is applied
  useEffect(() => {
    if (image && imgRef.current) {
      imgRef.current.cache();
    }
  }, [image, size, borderFilter]);

  const handleDragMove = (e) => onDragMove && onDragMove(id, e.target.x(), e.target.y());
  const handleContext = (e) => {
    e.evt && e.evt.preventDefault && e.evt.preventDefault();
    if (onContextMenu) onContextMenu(id, e);
  };

  return (
    <Group
      name={`utility-${id}`}
      x={x}
      y={y}
      draggable={draggable}
      onDragStart={() => onDragStart && onDragStart(id)}
      onDragMove={handleDragMove}
      onDragEnd={(e) => onDragEnd && onDragEnd(id, e.target.x(), e.target.y())}
      onContextMenu={handleContext}
    >
      {image && (
        <KonvaImage
          ref={imgRef}
          image={image}
          width={size}
          height={size}
          offset={{ x: size, y: size }}
          filters={[borderFilter]}
          listening
        />
      )}
      {/* badge in bottom right of icon */}
      {typeof number === "number" && (
        <>
          <Text
            x={size / 2 - 12}
            y={-size / 2 + 6}
            text={String(number)}
            fontSize={8}
            fontStyle="bold"
            fill="#FFFFFF"
            listening
            onContextMenu={(e) => {
              e.evt && e.evt.preventDefault && e.evt.preventDefault();
              // stop Konva / DOM propagation so parent doesn't handle the same context menu
              try {
                e.evt && e.evt.stopPropagation && e.evt.stopPropagation();
              } catch (err) {}
              e.cancelBubble = true;
              onBadgeContext && onBadgeContext(id, e);
            }}
            onClick={(e) => {
              // also open on left-click for touch compatibility
              try {
                e.evt && e.evt.stopPropagation && e.evt.stopPropagation();
              } catch (err) {}
              e.cancelBubble = true;
              onBadgeContext && onBadgeContext(id, e);
            }}
          />
        </>
      )}
    </Group>
  );
};

export default UtilityIcon;
